import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext, useErrorHandlerContext } from '@omnipkg/ui-kit-web';

import { useDeliveryAddressContext } from '@src/contexts/DeliveryAddressContext';
import { useDeviceInfoContext } from '@src/contexts/DeviceInfoContext';
import { useInputFieldsContext } from '@src/contexts/InputFieldsContext';
import { useOrderDataContext } from '@src/contexts/OrderDataContext';
import { useParcelContext } from '@src/contexts/ParcelContext';
import { useTariffsContext } from '@src/contexts/TariffsContext';
import { Path } from '@src/types/routes';

import { useCreateOrder } from '../utils/createOrder';
import { useGetProcessingUid } from '../utils/getProcessingUid';
import { TIMEOUT_GET_PROCESSING_UID } from './constants';

export function useCreateOrderHandler(): {
  isError: boolean;
  isLoading: boolean;
  createOrderHandler: () => void;
} {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const { token } = useAuthContext();
  const { deviceInfo } = useDeviceInfoContext();
  const { deliveryAddress } = useDeliveryAddressContext();
  const { selectedTariff } = useTariffsContext();
  const { parcelInfo } = useParcelContext();
  const { inputFields } = useInputFieldsContext();
  const { isRecipientPays, recordProcessingUid } = useOrderDataContext();
  const { recordOrderData } = useOrderDataContext();
  const { handleError } = useErrorHandlerContext();

  const createOrder = useCreateOrder();
  const getProcessingUid = useGetProcessingUid();

  const createOrderHandler = useCallback(() => {
    setIsError(false);

    if (!deviceInfo || !deliveryAddress || !selectedTariff) return;

    setIsLoading(true);

    createOrder({
      bearerToken: token,
      number: String(Date.now()),
      declaredCost: Number(parcelInfo.insurance),
      height: parcelInfo.height,
      width: parcelInfo.width,
      length: parcelInfo.length,
      weight: Number(parcelInfo.weight),
      tariffCode: selectedTariff.code,
      sum: isRecipientPays ? 0 : selectedTariff.delivery_cost,
      senderFirstName: inputFields.senderName,
      senderLastName: inputFields.senderSurname,
      senderPhone: inputFields.senderFullNumber,
      recipientFirstName: inputFields.recipientName,
      recipientLastName: inputFields.recipientSurname,
      recipientPhone: inputFields.recipientFullNumber,
      from: {
        code: deviceInfo.device,
        brand: deviceInfo.network,
      },
      to: {
        pointUid: deliveryAddress.uid,
      },
    })
      .then((data) => {
        recordOrderData(data);
        return data.order_uid;
      })
      .then((orderUid) => {
        setTimeout(() => {
          getProcessingUid({
            orderUid: orderUid as string,
          }).then(({ processing_uid }) => {
            recordProcessingUid(processing_uid);
          });
        }, TIMEOUT_GET_PROCESSING_UID);

        if (isRecipientPays) {
          return navigate(Path.PrintLabel);
        }
        navigate(Path.Payment);
      })
      .catch((error) => {
        if (error.message === 'permission_denied') {
          setIsError(true);
        }

        handleError(error.message);
      })
      .finally(() => setIsLoading(false));
  }, [
    token,
    deviceInfo,
    selectedTariff,
    isRecipientPays,
    deliveryAddress,
    parcelInfo.weight,
    parcelInfo.width,
    parcelInfo.height,
    parcelInfo.length,
    parcelInfo.insurance,
    inputFields.senderName,
    inputFields.senderSurname,
    inputFields.senderFullNumber,
    inputFields.recipientName,
    inputFields.recipientSurname,
    inputFields.recipientFullNumber,
    navigate,
    createOrder,
    handleError,
    recordOrderData,
    getProcessingUid,
    recordProcessingUid,
  ]);

  return { isError, isLoading, createOrderHandler };
}
